import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAddresses } from '../context/AddressContext';
import api from '../api';
import './LeaseLedgers.css';

const PROPERTY_TYPES = [
  { value: 'SingleFamily', label: 'Single Family' },
  { value: 'MultiFamily', label: 'Multi-Family' },
];

const LEASE_TYPES = ['Fixed', 'FixedWithRollover', 'AtWill'];
const RENT_CYCLES = ['Monthly', 'Weekly', 'Every2Weeks', 'Quarterly', 'Yearly', 'Every2Months', 'Daily', 'Every6Months', 'OneTime'];
const PAYMENT_METHODS = ['Check', 'Cash', 'MoneyOrder', 'CashierCheck', 'DirectDeposit', 'CreditCard', 'ElectronicPayment'];

export default function LeaseLedgers() {
  const { id: packageId } = useParams();
  const { addresses } = useAddresses();
  // eslint-disable-next-line no-unused-vars
  const _hasAddresses = addresses; // referenced for future auto-add UX

  const [properties, setProperties] = useState([]);
  const [leases, setLeases] = useState([]);
  const [paymentsByLease, setPaymentsByLease] = useState({});
  const [bankAccounts, setBankAccounts] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [statements, setStatements] = useState([]);
  const [exportingStatement, setExportingStatement] = useState({});
  const [bankAccountsError, setBankAccountsError] = useState('');
  const [glAccountsError, setGlAccountsError] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [pushingPropAll, setPushingPropAll] = useState(false);

  const propertySaveTimers = useRef({});
  const leaseSaveTimers = useRef({});

  const loadProperties = useCallback(async () => {
    try {
      const res = await api.get('/lease-ledger/properties', { params: { packageId } });
      setProperties(res.data.properties || []);
    } catch (err) {
      setGlobalError(err.response?.data?.error || 'Failed to load properties.');
    }
  }, [packageId]);

  const loadLeases = useCallback(async () => {
    try {
      const res = await api.get('/lease-ledger/leases', { params: { packageId } });
      setLeases(res.data.leases || []);
    } catch (err) {
      console.error('Failed to load leases:', err);
    }
  }, [packageId]);

  const loadBankAccounts = useCallback(async () => {
    try {
      const res = await api.get('/lease-ledger/buildium/bank-accounts');
      setBankAccounts(res.data.bankAccounts || []);
      setBankAccountsError('');
    } catch (err) {
      setBankAccountsError(err.response?.data?.error || 'Buildium bank accounts unavailable. Configure BUILDIUM_CLIENT_ID/SECRET in Doppler.');
    }
  }, []);

  const loadGlAccounts = useCallback(async () => {
    try {
      const res = await api.get('/lease-ledger/buildium/gl-accounts');
      setGlAccounts(res.data.glAccounts || []);
      setGlAccountsError('');
    } catch (err) {
      setGlAccountsError(err.response?.data?.error || 'Buildium GL accounts unavailable.');
    }
  }, []);

  const loadStatements = useCallback(async () => {
    try {
      const res = await api.get('/lease-ledger/statements', { params: { packageId } });
      setStatements(res.data.statements || []);
    } catch (err) {
      console.error('Failed to load tenant statements:', err);
    }
  }, [packageId]);

  useEffect(() => {
    loadProperties();
    loadBankAccounts();
    loadGlAccounts();
    loadStatements();
  }, [loadProperties, loadBankAccounts, loadGlAccounts, loadStatements]);

  // Re-load leases whenever the property list changes (so newly-pushed
  // properties or new units pull in their lease cards)
  useEffect(() => {
    loadLeases();
  }, [properties, loadLeases]);

  // ---- Property handlers ---------------------------------------------------

  const debouncedSaveProperty = (propId, payload) => {
    if (propertySaveTimers.current[propId]) clearTimeout(propertySaveTimers.current[propId]);
    propertySaveTimers.current[propId] = setTimeout(async () => {
      try {
        await api.put(`/lease-ledger/properties/${propId}`, payload);
      } catch (err) {
        console.error('Save property failed:', err);
      }
    }, 500);
  };

  const updateProperty = (propId, patch) => {
    setProperties(prev => prev.map(p => p.id === propId ? { ...p, ...patch } : p));
    debouncedSaveProperty(propId, patch);
  };

  const setPropertyUnits = (propId, units) => {
    setProperties(prev => prev.map(p => p.id === propId ? { ...p, units } : p));
    debouncedSaveProperty(propId, { units });
  };

  const addUnit = (prop) => {
    const next = [...(prop.units || []), { name: '', buildiumUnitId: null }];
    setPropertyUnits(prop.id, next);
  };

  const removeUnit = (prop, index) => {
    const next = (prop.units || []).filter((_, i) => i !== index);
    setPropertyUnits(prop.id, next);
  };

  const setUnitName = (prop, index, name) => {
    const next = (prop.units || []).map((u, i) => i === index ? { ...u, name } : u);
    setPropertyUnits(prop.id, next);
  };

  const addNewProperty = async () => {
    try {
      const res = await api.post('/lease-ledger/properties', { packageId, address: '' });
      setProperties(prev => [...prev, res.data.property]);
    } catch (err) {
      setGlobalError(err.response?.data?.error || 'Failed to add property.');
    }
  };

  const deleteProperty = async (prop) => {
    if (!window.confirm(`Delete "${prop.address || 'this property'}" from the lease ledger list?`)) return;
    try {
      await api.delete(`/lease-ledger/properties/${prop.id}`);
      setProperties(prev => prev.filter(p => p.id !== prop.id));
    } catch (err) {
      setGlobalError(err.response?.data?.error || 'Failed to delete property.');
    }
  };

  const pushProperty = async (prop) => {
    setProperties(prev => prev.map(p => p.id === prop.id ? { ...p, buildium_status: 'pending' } : p));
    try {
      const res = await api.post(`/lease-ledger/properties/${prop.id}/push-to-buildium`);
      setProperties(prev => prev.map(p => p.id === prop.id ? res.data.property : p));
    } catch (err) {
      const msg = err.response?.data?.error || 'Push failed.';
      setProperties(prev => prev.map(p => p.id === prop.id
        ? { ...p, buildium_status: 'failed', buildium_error: msg }
        : p));
    }
  };

  const pushAllProperties = async () => {
    setPushingPropAll(true);
    try {
      await api.post('/lease-ledger/properties/push-all', { packageId });
      await loadProperties();
    } catch (err) {
      setGlobalError(err.response?.data?.error || 'Push all failed.');
    } finally {
      setPushingPropAll(false);
    }
  };

  // ---- Lease handlers ------------------------------------------------------

  const debouncedSaveLease = (leaseId, leaseData) => {
    if (leaseSaveTimers.current[leaseId]) clearTimeout(leaseSaveTimers.current[leaseId]);
    leaseSaveTimers.current[leaseId] = setTimeout(async () => {
      try {
        await api.put(`/lease-ledger/leases/${leaseId}`, { lease_data: leaseData });
      } catch (err) {
        console.error('Save lease failed:', err);
      }
    }, 500);
  };

  const updateLeaseField = (leaseId, key, value) => {
    setLeases(prev => prev.map(l => {
      if (l.id !== leaseId) return l;
      const next = { ...l, lease_data: { ...(l.lease_data || {}), [key]: value } };
      debouncedSaveLease(leaseId, next.lease_data);
      return next;
    }));
  };

  const pushLease = async (lease) => {
    setLeases(prev => prev.map(l => l.id === lease.id ? { ...l, buildium_status: 'pending' } : l));
    try {
      const res = await api.post(`/lease-ledger/leases/${lease.id}/push-to-buildium`);
      setLeases(prev => prev.map(l => l.id === lease.id ? { ...l, ...res.data.lease } : l));
    } catch (err) {
      const msg = err.response?.data?.error || 'Push failed.';
      setLeases(prev => prev.map(l => l.id === lease.id
        ? { ...l, buildium_status: 'failed', buildium_error: msg }
        : l));
    }
  };

  // ---- Payment handlers ----------------------------------------------------

  const loadPayments = async (leaseId) => {
    try {
      const res = await api.get(`/lease-ledger/leases/${leaseId}/payments`);
      setPaymentsByLease(prev => ({ ...prev, [leaseId]: res.data.payments || [] }));
    } catch (err) {
      console.error('Load payments failed:', err);
    }
  };

  // Lazily fetch payments once each lease has been pushed to Buildium
  useEffect(() => {
    leases.forEach(l => {
      if (l.buildium_lease_id && paymentsByLease[l.id] === undefined) {
        loadPayments(l.id);
      }
    });
  // eslint-disable-next-line
  }, [leases]);

  const postPayment = async (leaseId, payment_data, resetForm) => {
    try {
      const res = await api.post(`/lease-ledger/leases/${leaseId}/post-payment`, { payment_data });
      setPaymentsByLease(prev => ({
        ...prev,
        [leaseId]: [res.data.payment, ...(prev[leaseId] || [])],
      }));
      resetForm();
      // Re-sync lease data so the Rent Payment sub-form default reverts
      // to the full rent amount after the prorated first payment clears.
      await loadLeases();
    } catch (err) {
      throw new Error(err.response?.data?.error || 'Post payment failed.');
    }
  };

  // ---- Tenant statement handlers -------------------------------------------

  const exportStatement = async (lease) => {
    setGlobalError('');
    setExportingStatement(s => ({ ...s, [lease.id]: true }));
    try {
      await api.post(`/lease-ledger/leases/${lease.id}/export-statement`);
      await loadStatements();
    } catch (err) {
      setGlobalError(err.response?.data?.error || 'Failed to export tenant statement.');
    } finally {
      setExportingStatement(s => ({ ...s, [lease.id]: false }));
    }
  };

  const downloadStatement = async (statement) => {
    try {
      const res = await api.get(`/lease-ledger/statements/${statement.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', statement.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setGlobalError('Failed to download tenant statement.');
    }
  };

  const deleteStatement = async (statement) => {
    if (!window.confirm(`Delete tenant statement for ${statement.address}?`)) return;
    try {
      await api.delete(`/lease-ledger/statements/${statement.id}`);
      await loadStatements();
    } catch {
      setGlobalError('Failed to delete tenant statement.');
    }
  };

  return (
    <div className="lease-ledgers-page">
      <h1 className="page-title">Lease Ledgers</h1>

      {globalError && <div className="error-box">{globalError}</div>}
      {bankAccountsError && <div className="warning-box">{bankAccountsError}</div>}

      <div className="ledgers-layout">
      <div className="ledgers-main">

      {/* ---- Section 1: Property Management ---- */}
      <section className="ledger-card">
        <div className="ledger-card-header">
          <h2>1. Properties</h2>
          <div className="ledger-card-actions">
            <button className="btn-secondary" onClick={addNewProperty}>+ Add Property</button>
            <button
              className="btn-primary"
              onClick={pushAllProperties}
              disabled={pushingPropAll || properties.length === 0}
            >
              {pushingPropAll ? 'Pushing...' : 'Add All Properties to Buildium'}
            </button>
          </div>
        </div>

        {properties.length === 0 ? (
          <div className="empty-row">No properties yet. Save addresses on the Addresses tab or click + Add Property.</div>
        ) : (
          <div className="property-rows">
            {properties.map(prop => (
              <PropertyRow
                key={prop.id}
                prop={prop}
                bankAccounts={bankAccounts}
                onUpdateAddress={(v) => updateProperty(prop.id, { address: v })}
                onUpdateType={(v) => updateProperty(prop.id, { property_type: v })}
                onUpdateBank={(v) => updateProperty(prop.id, { operating_bank_account_id: v })}
                onAddUnit={() => addUnit(prop)}
                onRemoveUnit={(i) => removeUnit(prop, i)}
                onSetUnitName={(i, name) => setUnitName(prop, i, name)}
                onDelete={() => deleteProperty(prop)}
                onPush={() => pushProperty(prop)}
              />
            ))}
          </div>
        )}
      </section>

      {/* ---- Section 2: Lease Creation ---- */}
      <section className="ledger-card">
        <div className="ledger-card-header">
          <h2>2. Leases</h2>
        </div>
        {glAccountsError && <div className="warning-box">{glAccountsError}</div>}
        {leases.length === 0 ? (
          <div className="empty-row">Add a property and push it to Buildium to create lease cards.</div>
        ) : (
          <div className="lease-rows">
            {leases.map(lease => (
              <LeaseRow
                key={lease.id}
                lease={lease}
                glAccounts={glAccounts}
                onUpdateField={(key, value) => updateLeaseField(lease.id, key, value)}
                onPush={() => pushLease(lease)}
              />
            ))}
          </div>
        )}
      </section>

      {/* ---- Section 3: Payment Entry ---- */}
      <section className="ledger-card">
        <div className="ledger-card-header">
          <h2>3. Payments</h2>
        </div>
        {leases.filter(l => l.buildium_lease_id).length === 0 ? (
          <div className="empty-row">Create a lease in Buildium to start posting payments.</div>
        ) : (
          <div className="payment-rows">
            {leases.filter(l => l.buildium_lease_id).map(lease => (
              <PaymentRow
                key={lease.id}
                lease={lease}
                glAccounts={glAccounts}
                payments={paymentsByLease[lease.id] || []}
                onPost={(payment_data, resetForm) => postPayment(lease.id, payment_data, resetForm)}
                onExport={() => exportStatement(lease)}
                exporting={!!exportingStatement[lease.id]}
              />
            ))}
          </div>
        )}
      </section>

      </div>

      <aside className="ledgers-sidebar">
        <h2 className="sidebar-title">Tenant Statements</h2>
        {statements.length === 0 ? (
          <p className="sidebar-empty">No statements exported yet. Click "Export Tenant Statement" on a lease in the Payments section.</p>
        ) : (
          <ul className="lease-list">
            {statements.map(st => (
              <li key={st.id} className="lease-list-item">
                <div className="lease-list-info">
                  <div className="lease-list-address">{st.address}</div>
                  <div className="lease-list-meta">{st.file_name}</div>
                </div>
                <div className="lease-list-actions">
                  <button
                    className="btn-icon"
                    onClick={() => downloadStatement(st)}
                    title="Download"
                  >
                    ↓
                  </button>
                  <button
                    className="btn-icon btn-icon-danger"
                    onClick={() => deleteStatement(st)}
                    title="Delete"
                  >
                    ×
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </aside>

      </div>
    </div>
  );
}

// ---- Subcomponents ---------------------------------------------------------

function PropertyRow({
  prop,
  bankAccounts,
  onUpdateAddress,
  onUpdateType,
  onUpdateBank,
  onAddUnit,
  onRemoveUnit,
  onSetUnitName,
  onDelete,
  onPush,
}) {
  const isMulti = prop.property_type === 'MultiFamily';
  const units = Array.isArray(prop.units) ? prop.units : [];

  return (
    <div className="property-row">
      <div className="property-row-main">
        <div className="ledger-field grow">
          <label>Address</label>
          <input
            type="text"
            value={prop.address || ''}
            onChange={e => onUpdateAddress(e.target.value)}
            placeholder="123 Main St, City, ST 12345, USA"
          />
        </div>
        <div className="ledger-field">
          <label>Type</label>
          <select value={prop.property_type} onChange={e => onUpdateType(e.target.value)}>
            {PROPERTY_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div className="ledger-field">
          <label>Operating Bank Account</label>
          <select
            value={prop.operating_bank_account_id || ''}
            onChange={e => onUpdateBank(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Select...</option>
            {bankAccounts.map(b => (
              <option key={b.Id} value={b.Id}>{b.Name}</option>
            ))}
          </select>
        </div>
        <button className="btn-icon btn-icon-danger" onClick={onDelete} title="Delete">×</button>
      </div>

      {isMulti && (
        <div className="property-units">
          <div className="property-units-label">Units</div>
          {units.length === 0 && <div className="empty-row small">No units. Click + Add Unit to start.</div>}
          {units.map((u, i) => (
            <div key={i} className="unit-row">
              <input
                type="text"
                value={u.name || ''}
                onChange={e => onSetUnitName(i, e.target.value)}
                placeholder="e.g. Unit 1, 1A, Front"
              />
              {u.buildiumUnitId && <span className="badge">Buildium #{u.buildiumUnitId}</span>}
              <button className="btn-icon btn-icon-danger" onClick={() => onRemoveUnit(i)} title="Remove unit">×</button>
            </div>
          ))}
          <button className="btn-secondary small" onClick={onAddUnit}>+ Add Unit</button>
        </div>
      )}

      <div className="property-row-footer">
        <PropertyStatus prop={prop} />
        <button
          className="btn-primary"
          onClick={onPush}
          disabled={prop.buildium_status === 'pending'}
        >
          {prop.buildium_status === 'pending'
            ? 'Pushing...'
            : prop.buildium_property_id
              ? 'Re-push to Buildium'
              : 'Push to Buildium'}
        </button>
      </div>
    </div>
  );
}

function PropertyStatus({ prop }) {
  if (prop.buildium_status === 'failed') {
    return <div className="status-pill status-failed">Error: {prop.buildium_error}</div>;
  }
  if (prop.buildium_property_id) {
    return <div className="status-pill status-ok">Buildium Property #{prop.buildium_property_id}</div>;
  }
  return <div className="status-pill status-idle">Not yet pushed</div>;
}

function LeaseRow({ lease, glAccounts, onUpdateField, onPush }) {
  const data = lease.lease_data || {};
  const ledgerLabel = lease.unit_label
    ? `${lease.property_address} — ${lease.unit_label}`
    : lease.property_address;

  return (
    <div className="lease-row">
      <h3 className="lease-row-title">{ledgerLabel}</h3>

      <div className="lease-fields">
        <div className="ledger-field">
          <label>Lease Type</label>
          <select value={data.leaseType || 'Fixed'} onChange={e => onUpdateField('leaseType', e.target.value)}>
            {LEASE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="ledger-field">
          <label>Lease From Date</label>
          <input type="date" value={data.leaseFromDate || ''} onChange={e => onUpdateField('leaseFromDate', e.target.value)} />
        </div>
        <div className="ledger-field">
          <label>Lease To Date</label>
          <input type="date" value={data.leaseToDate || ''} onChange={e => onUpdateField('leaseToDate', e.target.value)} />
        </div>
        <div className="ledger-field">
          <label>Rent Cycle</label>
          <select value={data.rentCycle || 'Monthly'} onChange={e => onUpdateField('rentCycle', e.target.value)}>
            {RENT_CYCLES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="ledger-field">
          <label>Rent Amount</label>
          <input type="number" value={data.rentAmount || ''} onChange={e => onUpdateField('rentAmount', e.target.value)} />
        </div>
        <div className="ledger-field">
          <label>Next Due Date</label>
          <input type="date" value={data.nextDueDate || ''} onChange={e => onUpdateField('nextDueDate', e.target.value)} />
        </div>
        <div className="ledger-field">
          <label>Security Deposit</label>
          <input type="number" value={data.securityDeposit || ''} onChange={e => onUpdateField('securityDeposit', e.target.value)} />
        </div>
        <div className="ledger-field">
          <label>Security Deposit Due Date</label>
          <input type="date" value={data.securityDepositDueDate || ''} onChange={e => onUpdateField('securityDepositDueDate', e.target.value)} />
        </div>
        <div className="ledger-field">
          <label>Tenant First Name</label>
          <input type="text" value={data.tenantFirstName || ''} onChange={e => onUpdateField('tenantFirstName', e.target.value)} />
        </div>
        <div className="ledger-field">
          <label>Tenant Last Name</label>
          <input type="text" value={data.tenantLastName || ''} onChange={e => onUpdateField('tenantLastName', e.target.value)} />
        </div>
        <div className="ledger-field">
          <label>Rent Income GL Account</label>
          <select
            value={data.rentGlAccountId || ''}
            onChange={e => onUpdateField('rentGlAccountId', e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">Select...</option>
            {glAccounts.map(g => (
              <option key={g.Id} value={g.Id}>{g.Name} ({g.Type})</option>
            ))}
          </select>
        </div>
        <div className="ledger-field checkbox">
          <label>
            <input
              type="checkbox"
              checked={data.prorateFirstMonth !== false}
              onChange={e => onUpdateField('prorateFirstMonth', e.target.checked)}
            />
            Prorate first month's rent
          </label>
        </div>
        <div className="ledger-field checkbox">
          <label>
            <input
              type="checkbox"
              checked={!!data.sendWelcomeEmail}
              onChange={e => onUpdateField('sendWelcomeEmail', e.target.checked)}
            />
            Send welcome email
          </label>
        </div>
      </div>

      <div className="lease-row-footer">
        {lease.buildium_status === 'failed' && (
          <div className="status-pill status-failed">Error: {lease.buildium_error}</div>
        )}
        {lease.buildium_lease_id && (
          <div className="status-pill status-ok">Buildium Lease #{lease.buildium_lease_id}</div>
        )}
        {!lease.buildium_lease_id && lease.buildium_status !== 'failed' && (
          <div className="status-pill status-idle">Not yet created</div>
        )}
        <button
          className="btn-primary"
          onClick={onPush}
          disabled={lease.buildium_status === 'pending'}
        >
          {lease.buildium_status === 'pending'
            ? 'Creating...'
            : lease.buildium_lease_id
              ? 'Re-create in Buildium'
              : 'Create Lease in Buildium'}
        </button>
      </div>
    </div>
  );
}

function PaymentRow({ lease, glAccounts, payments, onPost, onExport, exporting }) {
  const ledgerLabel = lease.unit_label
    ? `${lease.property_address} — ${lease.unit_label}`
    : lease.property_address;

  // Resolve the two default GL accounts from the Buildium list. Rent line
  // falls back to whatever GL account the lease card itself is configured
  // with so manual overrides on the lease flow through.
  const rentGl = glAccounts.find(g =>
    (g.Name || '').trim().toLowerCase() === 'rent income' &&
    (g.Type || '').toLowerCase() === 'income'
  );
  const depositGl = glAccounts.find(g =>
    (g.Name || '').trim().toLowerCase() === 'security deposit liability' &&
    (g.Type || '').toLowerCase() === 'liability'
  );

  return (
    <div className="payment-row">
      <div className="payment-row-header">
        <h3 className="lease-row-title">{ledgerLabel}</h3>
        <button
          className="btn-secondary"
          onClick={onExport}
          disabled={exporting}
          title="Export all transactions from the lease ledger as a tenant statement PDF"
        >
          {exporting ? 'Exporting...' : 'Export Tenant Statement'}
        </button>
      </div>

      <PaymentSubForm
        key={`rent-${lease.lease_data?.proratedFirstMonthAmount ?? 'full'}`}
        title="Rent Payment"
        defaultAmount={lease.lease_data?.proratedFirstMonthAmount || lease.lease_data?.rentAmount || ''}
        defaultGlAccountId={lease.lease_data?.rentGlAccountId || rentGl?.Id || ''}
        glAccounts={glAccounts}
        onPost={onPost}
      />

      <PaymentSubForm
        key="deposit"
        title="Security Deposit Payment"
        defaultAmount={lease.lease_data?.securityDeposit || ''}
        defaultGlAccountId={depositGl?.Id || ''}
        glAccounts={glAccounts}
        onPost={onPost}
      />

      {payments.length > 0 && (
        <div className="payment-history">
          <div className="payment-history-title">Posted Payments</div>
          <ul>
            {payments.map(p => (
              <li key={p.id}>
                {p.buildium_payment_id ? (
                  <span className="status-pill status-ok">#{p.buildium_payment_id}</span>
                ) : (
                  <span className="status-pill status-failed">{p.buildium_error || 'pending'}</span>
                )}
                {' '}
                ${p.payment_data?.amount} on {p.payment_data?.date} ({p.payment_data?.paymentMethod})
                {p.payment_data?.memo && <span className="payment-memo"> — {p.payment_data.memo}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PaymentSubForm({ title, defaultAmount, defaultGlAccountId, glAccounts, onPost }) {
  const todayISO = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(todayISO);
  const [paymentMethod, setPaymentMethod] = useState('Check');
  const [amount, setAmount] = useState(defaultAmount);
  const [glAccountId, setGlAccountId] = useState(defaultGlAccountId);
  const [memo, setMemo] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [sendEmailReceipt, setSendEmailReceipt] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');

  // Re-apply the default amount/GL account when they become available
  // (GL accounts load async, lease data can sync in after mount). Single-
  // dep array is intentional: only react to the default prop changing,
  // not to the user clearing the field afterwards.
  useEffect(() => {
    setAmount(prev => (prev ? prev : defaultAmount));
  }, [defaultAmount]);
  useEffect(() => {
    setGlAccountId(prev => (prev ? prev : defaultGlAccountId));
  }, [defaultGlAccountId]);

  const reset = () => {
    setMemo('');
    setReferenceNumber('');
    setError('');
  };

  const submit = async () => {
    setError('');
    setPosting(true);
    try {
      await onPost(
        { date, paymentMethod, amount, glAccountId, memo, referenceNumber, sendEmailReceipt },
        reset,
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="payment-subform">
      <div className="payment-subform-title">{title}</div>
      <div className="payment-fields">
        <div className="ledger-field">
          <label>Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <div className="ledger-field">
          <label>Payment Method</label>
          <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
            {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="ledger-field">
          <label>Amount</label>
          <input type="number" value={amount} onChange={e => setAmount(e.target.value)} />
        </div>
        <div className="ledger-field">
          <label>GL Account</label>
          <select value={glAccountId} onChange={e => setGlAccountId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Select...</option>
            {glAccounts.map(g => (
              <option key={g.Id} value={g.Id}>{g.Name} ({g.Type})</option>
            ))}
          </select>
        </div>
        <div className="ledger-field">
          <label>Reference #</label>
          <input type="text" value={referenceNumber} onChange={e => setReferenceNumber(e.target.value)} />
        </div>
        <div className="ledger-field grow">
          <label>Memo</label>
          <input type="text" value={memo} onChange={e => setMemo(e.target.value)} />
        </div>
        <div className="ledger-field checkbox">
          <label>
            <input
              type="checkbox"
              checked={sendEmailReceipt}
              onChange={e => setSendEmailReceipt(e.target.checked)}
            />
            Send email receipt
          </label>
        </div>
      </div>

      {error && <div className="error-box">{error}</div>}

      <div className="payment-row-footer">
        <button className="btn-primary" onClick={submit} disabled={posting}>
          {posting ? 'Posting...' : `Post ${title} to Ledger`}
        </button>
      </div>
    </div>
  );
}
